import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { DetailsContent } from "components/app_layout/DetailsLayout";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import FilterPanelToggle from "components/search_page/FilterPanelToggle";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import Input from "components/ui/input/Input";
import Sidebar from "components/ui/sidebar/SidebarOld";
import Badge from "lib/core_components/Badge";
import { useDebouncedValue } from "lib/debounce";
import { useContactSearch } from "lib/hooks/api/contacts/useContactSearch";
import { useSeniorityTierOptions } from "lib/hooks/api/contacts/useSeniorityTierOptions";
import { TableKey, useTablePageSize } from "lib/hooks/useTablePageSize";
import { useURLState } from "lib/hooks/useURLState";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { EventData, EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { pluralise } from "lib/utils";
import {
  buildContactSearchInput,
  CognismContactFilters,
  CognismContactFiltersSchema,
  CONTACT_FILTER_GROUP_KEYS,
  ContactFilterGroupKey,
  countActiveFilters,
  countActiveFiltersForGroups,
  EMPTY_CONTACT_FILTERS,
} from "./cognismContactFilters";
import CognismContactsActionBar from "./CognismContactsActionBar";
import CognismContactsFilterDrawer from "./CognismContactsFilterDrawer";
import CognismContactsFilterPanel from "./CognismContactsFilterPanel";
import CognismContactsTableView, { HideableContactColumn } from "./CognismContactsTableView";
import { withSelectionKeys } from "./contactSource";
import { ALL_FILTERS_CLEARED, contactFilterEvents } from "./contactTracking";
import ExportCognismContactsButton from "./ExportCognismContactsButton";
import { useCursorPagination } from "./useCursorPagination";

/**
 * Configuration for embedding the table inside another surface (e.g. the buyer
 * profile), scoped to a fixed set of buyers. All fields are required so an embed
 * can't be half-configured; omitting the whole prop keeps the standalone
 * /contacts behaviour byte-identical.
 */
export type CognismContactsEmbedConfig = {
  /**
   * Buyer scope merged into the search input only — never filter state or the URL,
   * so "Clear all" and stale URL blobs can't unlock or widen the scope. Non-empty by
   * type: an empty array would render the embedded shell (Buyers filter hidden) with
   * nothing actually locking the scope.
   */
  lockedBuyerGuids: [string, ...string[]];
  defaultPageSize: number;
  /** URL param for filter state; must differ from /contacts' "contactFilters". */
  urlStateKey: string;
  tableKey: TableKey;
  /** Analytics identity for the subscription check + all table events. */
  contextSource: string;
  /** Rendered under the zero-results empty state (e.g. a request-contacts CTA). */
  emptyStateAction?: ReactNode;
};

// Embedded surfaces lock the buyer scope and search by name inline, so those
// filter sections are hidden from the drawer.
const EMBED_HIDDEN_SECTIONS: ContactFilterGroupKey[] = ["buyers", "contactName"];

// Under a single-buyer scope every row repeats the same buyer and buyer type, so both
// columns say nothing — dropped in favour of the width. Module-level (rather than derived
// inline) to keep the table view's columns memo stable across renders.
const SINGLE_BUYER_HIDDEN_COLUMNS: HideableContactColumn[] = ["buyer", "buyerType"];

/**
 * Container for the unified contacts table (APP-4670 search, APP-4674 reveal).
 *
 * Owns filter + cursor pagination state, the unified `contactSearch` query (both
 * Oscar and Cognism, badged by source), and row selection (keyed by a composite
 * `source:nativeId` so it's unique across sources).
 * Filters persist to the URL (compressed) so they survive a reload; cursor +
 * selection stay ephemeral. `keepPreviousData` keeps the current page visible
 * while the next/previous page loads. Changing a filter resets to the first page.
 *
 * Renders one of two layout shells: the standalone page's sidebar layout, or an
 * embedded layout (inline search + filter drawer) when `embedConfig` is set. The
 * standalone one waits for a filter before searching at all — see `isUnfiltered`.
 */
function CognismContactsTable({ embedConfig }: { embedConfig?: CognismContactsEmbedConfig }) {
  const contextSource = embedConfig?.contextSource ?? "Buyer contacts table";

  // Contacts is a paywalled data type — gate the whole table behind CONTACTS access
  // (mirrors the legacy ContactsTable).
  const { authorised: hasContacts } = useCheckSubscription("CONTACTS", {
    "Context source": contextSource,
  });

  // Persisted per-table page size; cursor pagination has no size selector, so
  // we only read the stored value.
  const [pageSize] = useTablePageSize(
    embedConfig?.tableKey ?? "cognism-contacts-table",
    embedConfig?.defaultPageSize ?? 25,
  );
  const { after, canGoPrevious, goNext, goPrevious, reset } = useCursorPagination();
  const [filters = EMPTY_CONTACT_FILTERS, setFilters] = useURLState(
    embedConfig?.urlStateKey ?? "contactFilters",
    EMPTY_CONTACT_FILTERS,
    CognismContactFiltersSchema,
    { encoder: "compress", replaceState: true },
  );
  // Selection keys are composite `source:nativeId` so they're unique across both
  // sources (Cognism redeemId, Oscar oscar_contacts.id).
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // Standalone: filters live in a collapsible left sidebar (Notices 2.0 layout), open
  // by default. Embedded: filters live in a drawer, closed until the Filters button.
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Seniority tier options (value/label) are backend-served — the single source of truth
  // (@stotles/data seniorityMapping). Feeds the filter dropdown + the column label map.
  // Gated on CONTACTS: the resolver 403s (with an error toast) for unauthorised users.
  const { data: seniorityTierOptions } = useSeniorityTierOptions({ enabled: hasContacts });
  const seniorityTierLabels = useMemo(
    () => Object.fromEntries((seniorityTierOptions ?? []).map((o) => [o.value, o.label])),
    [seniorityTierOptions],
  );

  // Rendered outside this table's own TrackingProvider, so the context source is passed in.
  const { logEvent } = useTracking();
  const logFilterEvent = (data: EventData) =>
    logEvent(EventNames.filterActioned, { ...data, "Context source": contextSource });
  // The last settled name search reported. Starts from the loaded value, so a search restored
  // from the URL isn't reported as a new one.
  const reportedSearchQuery = useRef(filters.searchQuery.trim());

  // Selection is per-page: the action bar only acts on contacts on the current
  // page, so clear it whenever the visible page changes (filter/page nav) to
  // avoid orphaned selections with no action bar.
  const onFiltersChange = (next: CognismContactFilters) => {
    const events = contactFilterEvents(filters, next, seniorityTierLabels);
    events.forEach(logFilterEvent);
    // A name search cleared along with the rest is part of "All filters", not its own clear.
    if (events.includes(ALL_FILTERS_CLEARED) && !next.searchQuery.trim()) {
      reportedSearchQuery.current = "";
    }
    setFilters(next);
    setSelectedKeys([]);
    reset();
  };

  const onNext = () => {
    setSelectedKeys([]);
    goNext(connection?.pageInfo.endCursor ?? null);
  };

  const onPrevious = () => {
    setSelectedKeys([]);
    goPrevious();
  };

  // Debounce only the free-text name box so typing doesn't fire a query per
  // keystroke; the toggles/buyer filters apply immediately.
  const [debouncedSearchQuery] = useDebouncedValue(filters.searchQuery.trim(), 400);

  // The name search reports once typing settles, not per keystroke. The text itself is a
  // person's name, so only the action is sent.
  useEffect(() => {
    if (debouncedSearchQuery === reportedSearchQuery.current) return;
    reportedSearchQuery.current = debouncedSearchQuery;
    logFilterEvent({
      "Filter name": "Name search",
      "Action type": debouncedSearchQuery ? "Filter applied" : "Filter cleared",
    });
    // Only the settled query should trigger this; `logFilterEvent` is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  const effectiveFilters = { ...filters, searchQuery: debouncedSearchQuery };

  const searchInput = buildContactSearchInput(effectiveFilters, {
    first: pageSize,
    after,
    lockedBuyerGuids: embedConfig?.lockedBuyerGuids,
  });

  // Standalone /contacts has no implicit scope, so an unfiltered search returns the whole
  // index — wait for intent instead. Embedded surfaces are already scoped by lockedBuyerGuids.
  // Counted on the debounced filters so the first keystroke doesn't fire that search anyway.
  const isUnfiltered = !embedConfig && countActiveFilters(effectiveFilters) === 0;

  const { data, isLoading, isFetching, isError } = useContactSearch(searchInput, {
    keepPreviousData: true,
    enabled: hasContacts && !isUnfiltered,
  });

  const connection = data?.contactSearch;
  const totalResults = connection?.totalResults ?? null;
  const rows = withSelectionKeys(connection?.nodes ?? []);
  const selectedContacts = rows.filter((c) => selectedKeys.includes(c.selectionKey));

  // Hidden sections (locked buyer scope, inline name search) are not user state
  // here, so they never count as active filters on an embedded surface.
  const visibleGroups = embedConfig
    ? CONTACT_FILTER_GROUP_KEYS.filter((key) => !EMBED_HIDDEN_SECTIONS.includes(key))
    : CONTACT_FILTER_GROUP_KEYS;
  const activeFilterCount = countActiveFiltersForGroups(filters, visibleGroups);

  if (!hasContacts) {
    return <PaywallBanner featureType="CONTACTS" />;
  }

  // Shared children across both layout shells. The count and the export both describe a
  // search that hasn't run while unfiltered, so they're withheld along with the results.
  // Results always ship sorted by contact name — the search takes no sort.
  const resultsHeader = !isUnfiltered && totalResults != null && (
    <Text>{pluralise(totalResults, "Result", true)} • Sorted by Contact name</Text>
  );
  const exportButton = !isUnfiltered && (
    <ExportCognismContactsButton
      selectedContacts={selectedContacts}
      totalResults={totalResults}
      filter={searchInput}
    />
  );
  const actionBar = selectedContacts.length > 0 && (
    <CognismContactsActionBar
      selectedContacts={selectedContacts}
      onClearSelection={() => setSelectedKeys([])}
      filter={searchInput}
      totalResults={totalResults}
    />
  );
  const tableView = (
    <CognismContactsTableView
      contacts={rows}
      pageSize={pageSize}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      totalResults={totalResults}
      hasNextPage={connection?.pageInfo.hasNextPage ?? false}
      canGoPrevious={canGoPrevious}
      onNext={onNext}
      onPrevious={onPrevious}
      selectedKeys={selectedKeys}
      onSelectionChange={setSelectedKeys}
      seniorityTierLabels={seniorityTierLabels}
      emptyStateAction={embedConfig?.emptyStateAction}
      hiddenColumns={
        embedConfig?.lockedBuyerGuids.length === 1 ? SINGLE_BUYER_HIDDEN_COLUMNS : undefined
      }
    />
  );
  // Shown instead of the table until the user filters. The CTA only appears with the sidebar
  // collapsed, so there's always a visible route to the filters.
  const startingState = (
    <Flex justifyContent="center" width="100%">
      <EmptyState
        type="startingState"
        heading="Search for contacts"
        description="Apply a filter to find decision-makers at the buyers you care about."
        action={
          !isSidebarOpen && (
            <Button
              buttonType="secondary"
              icon="filterFunnel02"
              onClick={() => setIsSidebarOpen(true)}
            >
              Show filters
            </Button>
          )
        }
        yAxisMargin="64px"
      />
    </Flex>
  );

  if (embedConfig) {
    // Embedded shell (e.g. the buyer profile contacts tab): plain flow layout with
    // an inline name search + Filters-button-and-drawer, matching the other buyer
    // tabs. No DetailsContent — the host page owns scrolling and padding.
    return (
      <TrackingProvider data={{ "Context source": contextSource }}>
        <Flex column gap={16}>
          <Flex justifyContent="space-between" alignItems="center" gap={16}>
            <Flex alignItems="center" gap={16}>
              <Flex gap={8}>
                <SearchInputWrapper>
                  <Input
                    name="contactSearchQuery"
                    aria-label="Search by name"
                    placeholder="Search by name"
                    value={filters.searchQuery}
                    onChange={(e) => onFiltersChange({ ...filters, searchQuery: e.target.value })}
                  />
                </SearchInputWrapper>
                <Button
                  buttonType="secondary"
                  icon="filterFunnel02"
                  onClick={() => setIsFilterDrawerOpen(true)}
                >
                  Filters
                  {activeFilterCount > 0 && <Badge size={18}>{activeFilterCount}</Badge>}
                </Button>
              </Flex>
              {resultsHeader}
            </Flex>
            {exportButton}
          </Flex>
          {actionBar}
          {tableView}
          <CognismContactsFilterDrawer
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            filters={filters}
            onChange={onFiltersChange}
            hiddenSections={EMBED_HIDDEN_SECTIONS}
          />
        </Flex>
      </TrackingProvider>
    );
  }

  return (
    <TrackingProvider data={{ "Context source": contextSource }}>
      <PageContent>
        <PageMainRow>
          <Sidebar open={isSidebarOpen} fillContainer>
            <CognismContactsFilterPanel
              filters={filters}
              onChange={onFiltersChange}
              onCollapse={() => setIsSidebarOpen(false)}
            />
          </Sidebar>
          <TableMainArea>
            <Flex column gap={16}>
              <Flex justifyContent="space-between" alignItems="center" gap={16}>
                <Flex alignItems="center" gap={16}>
                  <FilterPanelToggle
                    open={isSidebarOpen}
                    onOpenChange={setIsSidebarOpen}
                    appliedFiltersCount={activeFilterCount}
                  />
                  {resultsHeader}
                </Flex>
                {exportButton}
              </Flex>
              {actionBar}
              {isUnfiltered ? startingState : tableView}
            </Flex>
          </TableMainArea>
        </PageMainRow>
      </PageContent>
    </TrackingProvider>
  );
}

// Two-column shell mirroring Notices 2.0: a collapsible filter sidebar on the left and
// an independently-scrolling results column on the right. `.pageScrollableContent` is a
// flex column, so the sidebar fills the available height (fillContainer) without needing
// to measure the sticky header.
const PageContent = styled(DetailsContent)({
  padding: 0,
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
});

const PageMainRow = styled(Flex)({
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
});

const TableMainArea = styled.div({
  flex: "1 1 0",
  minWidth: 0,
  // Allow this flex item to shrink below content height so overflow can scroll.
  minHeight: 0,
  overflowY: "auto",
  padding: "16px 24px",
});

const SearchInputWrapper = styled.div({
  width: 320,
});

export default CognismContactsTable;
